// 3rd party
import * as React from 'react';
import { observer } from 'mobx-react';
import * as debounce from 'lodash.debounce';

// UI Toolkit
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkInformativeInput, UitkTextArea } from 'uitk-react-input';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkParagraph } from 'uitk-react-text';
import { MoreInformationDialog, MoreInformationDialogTriggerButton } from 'components/shared/more-information';

// Models
import { ProductV2 } from 'models';

// Shared UI
import { useLocalization, LocalizedText } from '@shared-ui/localization-context';

// Hooks
import { useStores } from 'hooks';

// Utils
import {
  analyticsEvent,
  getProgramAnalyticsMicroMessage,
  getProductAnalyticsMicroMessage,
  getUserAnalyticsMicroMessage,
} from '../../util/analytics';

interface VrboSearchV2Props {
  product?: ProductV2;
}

interface VrboSearchV2State {
  pubref: string;
  bufferedPubref: string;
  errors?: {
    pubref?: string;
  };
}

const VrboSearchV2 = observer(
  // tslint:disable-next-line:no-shadowed-variable
  function VrboSearchV2({ product }: VrboSearchV2Props): JSX.Element {
    const l10n = useLocalization();
    const { analytics, userAuth, user, pzStore } = useStores();
    const { activeProgram } = user;

    const specialChars = ['>', '<', '&', "'", '"'];

    const [state, setState] = React.useState<VrboSearchV2State>({
      pubref: '',
      bufferedPubref: '',
      errors: {
        pubref: '',
      },
    });

    const copyToClipboardLabel = l10n.formatText('product.search.code.actions.copy_to_clipboard');
    const copiedToClipboardLabel = l10n.formatText('product.search.code.actions.copied_to_clipboard');

    function pasteToClipboard(copyText: string): void {
      navigator.clipboard.writeText(copyText);

      const copyButton = document.getElementById('clipboardCopyBtn');
      copyButton.textContent = copiedToClipboardLabel;
      setTimeout(() => {
        copyButton.textContent = copyToClipboardLabel;
      }, 1500);
    }

    function analyticsTrackEvents(rfrr: string): void {
      const microMessages = [
        getProgramAnalyticsMicroMessage(activeProgram, activeProgram?.programStatus),
        getProductAnalyticsMicroMessage(product),
      ];
      const filterAnalyticsTrackEvent = {
        'affhub.product_config': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Product Config Presented', microMessages);
        },
        'affhub_copy_code.selected': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Copy Code Selected', microMessages);
        },
        'affhub_implementation_guide.selected': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Implementation Guide Selected', microMessages);
        },
      };

      filterAnalyticsTrackEvent[rfrr]();
    }

    function renderDocumentationLink(): JSX.Element {
      const trackImplementationGuideLink = {
        id: 'affhub_implementation_guide.selected',
        callback: () => {
          analyticsTrackEvents('affhub_implementation_guide.selected');
        },
      };

      return (
        <UitkSpacing margin={{ blockend: 'six' }}>
          <UitkParagraph size={1}>
            <UitkLink inline={true} analytics={trackImplementationGuideLink}>
              <a href={product.guideLink} target="_blank" rel="noreferrer">
                <LocalizedText message="product.search.code.implementation_guide" />
                <UitkIcon name="open_in_new" />
              </a>
            </UitkLink>
          </UitkParagraph>
        </UitkSpacing>
      );
    }

    const setBufferedPubref = debounce(
      // tslint:disable-next-line:no-shadowed-variable
      function setBufferedPubref(bufferedPubref: string) {
        setState((prevState) => {
          return { ...prevState, bufferedPubref };
        });
      },
      250,
    );

    function handlePubrefChange(event): void {
      const {
        value,
        validity: { valid },
      } = event.target;

      setState((prevState) => {
        return {
          ...prevState,
          pubref: value,
          errors: {
            pubref: !valid && l10n.formatText('product.form.error.special_chars_invalid', specialChars),
          },
        };
      });

      setBufferedPubref(value);
    }

    function renderCodeMarkup(): JSX.Element {
      const codeMarkup =
        '<div' +
        ' class="ha-widget"' +
        ` data-site="${product.data.site}"` +
        ` data-locale="${product.data.locale}"` +
        ' data-widget="search"' +
        ' data-layout="headlined"' +
        ` data-theme="${product.data.theme}"` +
        ' data-network="pz"' +
        ` data-camref="${activeProgram?.camRef}"` +
        ` data-pubref="${state.bufferedPubref}"` +
        '></div>\n' +
        '<script' +
        ' class="ha-widget-script"' +
        ' src="https://csvcus.homeaway.com/rsrcs/cdn-affiliate-widgets/1.5.0/affiliate-widgets.min.js"' +
        '></script>';

      return (
        <UitkTextArea
          id="searchWidgetCode"
          label={l10n.formatText('product.search.code.form.markup.label')}
          value={codeMarkup}
          hasVisibleLabel={false}
        />
      );
    }

    function getIframeSrc(): string {
      return `${product.demoLink}?layout=headlined&theme=${product.data.theme}&network=pz&camref=${activeProgram?.camRef}&pubref=`;
    }

    function renderPreviewFrame(): JSX.Element {
      return (
        <>
          <link
            className="ha-widget-style"
            rel="stylesheet"
            href="https://csvcus.homeaway.com/rsrcs/cdn-affiliate-widgets/1.5.0/affiliate-widgets.min.css"
          />
          <div
            className="ha-widget ha-widget-search"
            data-site={product.data.site}
            data-locale={product.data.locale}
            data-widget="search"
            data-layout="headlined"
            data-theme={product.data.theme}
            data-network="pz"
            data-camref={activeProgram?.camRef}
            data-pubref=""
          >
            <iframe className="ha-widget-frame" src={getIframeSrc()} />
          </div>
          {/*<script*/}
          {/*  className="ha-widget-script"*/}
          {/*  src="https://csvcus.homeaway.com/rsrcs/cdn-affiliate-widgets/1.5.0/affiliate-widgets.min.js"*/}
          {/*/>*/}
        </>
      );
    }

    React.useEffect(() => {
      analyticsTrackEvents('affhub.product_config');
    });

    return (
      <article>
        <UitkLayoutGrid columns={{ large: 2 }} space="twelve">
          <UitkLayoutGridItem>
            <div>
              <UitkLayoutGrid space="six">
                <UitkLayoutGridItem>
                  <div>
                    <fieldset>
                      <UitkSpacing margin={{ blockend: 'four' }}>
                        <legend>
                          <UitkHeading tag="h4" size={4}>
                            <LocalizedText message="product.form.tracking-information.pz.heading.partnerize_tracking_information" />
                          </UitkHeading>
                        </legend>
                      </UitkSpacing>

                      <UitkLayoutGrid>
                        <UitkLayoutGridItem>
                          <div>
                            <UitkInformativeInput
                              id="tracking-pubref"
                              name="pubref"
                              label={l10n.formatText('product.form.tracking-information.pz.label.pubref_optional')}
                              informativeTrigger={<MoreInformationDialogTriggerButton id="pz-pubref" />}
                              a11yInformativeTrigger={l10n.formatText(
                                'more-information.dialog.trigger.more_information',
                              )}
                              value={state.pubref}
                              onChange={handlePubrefChange}
                              pattern={`[^${specialChars.join('')}]*`}
                              invalid={state.errors?.pubref}
                            />
                            <MoreInformationDialog
                              id="pz-pubref"
                              title={l10n.formatText('more-information.dialog.pz-pubref.title.what_pubref')}
                              description={l10n.formatText(
                                'more-information.dialog.pz-pubref.description.pubref_optional_reference_include_tracking_link_for_your_reporting_purposes_pubref_tracks_clicks_through_bookings',
                              )}
                            />
                          </div>
                        </UitkLayoutGridItem>
                      </UitkLayoutGrid>
                    </fieldset>
                  </div>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem>
                  <div>
                    <UitkHeading tag="h4" size={4}>
                      <LocalizedText message="product.search.code.title" />
                    </UitkHeading>
                    <UitkParagraph size={1}>
                      <LocalizedText message="product.search.code.instructions" />
                    </UitkParagraph>
                    {renderDocumentationLink()}
                    {renderCodeMarkup()}
                    <UitkParagraph size={1}>
                      <UitkSecondaryButton
                        id="clipboardCopyBtn"
                        onClick={() => {
                          const textArea = document.getElementById('searchWidgetCode') as HTMLTextAreaElement;
                          textArea.focus();
                          pasteToClipboard(textArea.value);
                          analyticsTrackEvents('affhub_copy_code.selected');
                        }}
                      >
                        {copyToClipboardLabel}
                      </UitkSecondaryButton>
                    </UitkParagraph>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem>
            <div>
              <UitkHeading tag="h4" size={4}>
                <LocalizedText message="product.search.preview.title" />
              </UitkHeading>
              <UitkSpacing margin={{ blockend: 'six' }}>
                <UitkParagraph size={1}>
                  <LocalizedText message="product.search.preview.information" />
                </UitkParagraph>
              </UitkSpacing>
              {renderPreviewFrame()}
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </article>
    );
  },
);

export default VrboSearchV2;
export { VrboSearchV2 };
